// servizi/amici.ts — v033: amici, «Disponibili ora», chi può sfidarti, amicizia a fine partita.
// Il numero delle richieste di amicizia arrivate lo porta il controllo delle richieste (Richieste.tsx, ogni pochi
// secondi): qui c'è solo il contatore che la Home mostra sul pulsante «Amici».
import { useSyncExternalStore } from "react";
import { richiesta } from "./api.ts";
import { idDispositivo } from "./dispositivo.ts";
import { avatarAttuale } from "./account.ts";
import type { SfidaCreata } from "./sfide.ts";

const enc = encodeURIComponent;

export interface Persona {
  id?: number;
  nome: string;
  nomeUtente: string | null;
  avatar: number | null;
}
export interface Amico extends Persona {
  id: number;
  online: boolean;
  sfidabile: boolean;
}
export interface ElencoAmici {
  amici: Amico[];
  ricevute: (Persona & { id: number })[];
  inviate: (Persona & { id: number })[];
}
export type StatoAmicizia = "nessuna" | "inviata" | "ricevuta" | "amici";

export const leggiAmici = () => richiesta<ElencoAmici>("/api/v1/amici");
export const leggiDisponibili = (nomi?: readonly string[]) =>
  richiesta<{ persone: Persona[]; puoi: boolean }>(`/api/v1/disponibili${nomi?.length ? `?nomi=${enc(nomi.join(","))}` : ""}`);
export const accettaAmicizia = (id: number) => richiesta<{ stato: "amici" }>(`/api/v1/amici/${id}/accetta`, { metodo: "POST" });
export const rifiutaAmicizia = (id: number) => richiesta<void>(`/api/v1/amici/${id}/rifiuta`, { metodo: "POST" });
export const togliAmico = (id: number) => richiesta<void>(`/api/v1/amici/${id}`, { metodo: "DELETE" });

// --- v039: persone bloccate (dall'account: restano bloccate anche se cambiano nome) ---------------------------------
export interface Bloccato extends Persona {
  id: number;
  /** quando l'ho bloccata (ISO) */
  il: string;
}
export const leggiBloccati = () => richiesta<{ voci: Bloccato[] }>("/api/v1/io/bloccati");
/** Blocca senza segnalare: per id (amici) o per nome utente (profilo pubblico). Toglie anche l'amicizia. */
export const bloccaPersona = (chi: { id: number } | { nome: string }) => richiesta<void>("/api/v1/io/bloccati", { metodo: "POST", corpo: chi });
export const sbloccaPersona = (id: number) => richiesta<void>(`/api/v1/io/bloccati/${id}`, { metodo: "DELETE" });
export const chiediAmiciziaProfilo = (nomeUtente: string) =>
  richiesta<{ stato: "inviata" | "amici" }>(`/api/v1/amici/nome/${enc(nomeUtente)}`, { metodo: "POST" });
export const amiciziaPartita = (codice: string) =>
  richiesta<{ stato: StatoAmicizia | "spenta" | "senza_account" | "altro_senza_account" }>(`/api/v1/sfide/${enc(codice)}/amicizia`);
export const chiediAmiciziaPartita = (codice: string) =>
  richiesta<{ stato: "inviata" | "amici" }>(`/api/v1/sfide/${enc(codice)}/amicizia`, { metodo: "POST" });

/** Sfida a un amico (arriva come le richieste dal link personale). */
export function sfidaAmico(id: number, mioNome: string): Promise<SfidaCreata & { per: { nome: string; nomeUtente: string } }> {
  return richiesta(`/api/v1/amici/${id}/sfida`, { metodo: "POST", corpo: { nome: mioNome, dispositivo: idDispositivo(), avatar: avatarAttuale() } });
}

/** Chi può sfidarti: nessuno, chi ha il mio link, chiunque. */
export type ChiPuoSfidare = "nessuno" | "link" | "tutti";
export function impostaChiPuoSfidare(c: ChiPuoSfidare): Promise<{ attivo: boolean; tutti: boolean }> {
  return richiesta("/api/v1/io/link", { metodo: "PUT", corpo: { attivo: c !== "nessuno", tutti: c === "tutti" } });
}

// --- contatore delle richieste di amicizia arrivate ----------------------------------------------------------------
let ricevute = 0;
const ascoltatori = new Set<() => void>();
export function impostaRicevute(n: number): void {
  if (n === ricevute) return;
  ricevute = n;
  ascoltatori.forEach((f) => f());
}
export function useRicevute(): number {
  return useSyncExternalStore(
    (f) => {
      ascoltatori.add(f);
      return () => ascoltatori.delete(f);
    },
    () => ricevute,
  );
}

// --- v037: chi mi ha portato qui e la classifica della settimana tra amici --------------------------------------
export interface Invitante extends Persona {
  id: number;
  stato: StatoAmicizia;
}
export const leggiInvitante = () => richiesta<{ invitante: Invitante | null }>(`/api/v1/amici/invitante?dispositivo=${enc(idDispositivo())}`);
export const chiediAmiciziaInvitante = () =>
  richiesta<{ stato: "inviata" | "amici" }>("/api/v1/amici/invitante", { metodo: "POST", corpo: { dispositivo: idDispositivo() } });
export interface RigaSettimana {
  id: number;
  nome: string;
  avatar: number | null;
  partite: number;
  vinte: number;
  io: boolean;
}
export const leggiSettimanaAmici = () => richiesta<{ classifica: RigaSettimana[] }>("/api/v1/amici/settimana");
